// ADR-132: append-only change history of a provider's mapping set (History tab).
// One group per set version (a save or an import): who / when / version once,
// then one compact line per changed title. Newest first.
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip } from '@mui/material';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import { useAlert } from '../../../contexts/AlertContext';
import { ingestionConfigurationApi } from '../../../services/api/ingestionConfigurationApi';
import { INGESTION_MAPPING_HISTORY_ACTION_LABELS } from '../../../constants/ingestion';
import { describeHistoryRow, groupHistoryByVersion } from '../../../utils/ingestionMappings';
import { getChipSx } from '../../reports/DirectManagerDiscrepancies/chipStyles';
import {
  HistoryGroup, HistoryGroupHeader, HistoryEntry, HistoryChange, HistorySeparator, PatternCell,
} from './styles';

const ACTION_COLORS = {
  CREATE: 'success',
  IMPORT_CREATE: 'success',
  UPDATE: 'info',
  IMPORT_UPDATE: 'info',
  DELETE: 'error',
  IMPORT_DELETE: 'error',
};

// Inside a group the "(import)" suffix is redundant: the header says it.
const ACTION_SHORT_LABELS = { CREATE: 'Added', UPDATE: 'Changed', DELETE: 'Removed' };
const shortLabel = (action) => ACTION_SHORT_LABELS[String(action).replace(/^IMPORT_/, '')]
  || INGESTION_MAPPING_HISTORY_ACTION_LABELS[action] || action;

/** `refreshKey` (the set version) reloads the list after a change on the Mappings tab. */
const HistoryPanel = ({ provider, refreshKey }) => {
  const { showAlert } = useAlert();
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState({ hasNextPage: false, endCursor: null, totalCount: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (cursor = null) => {
    try {
      setLoading(true);
      const data = await ingestionConfigurationApi.getJobPositionMappingHistory(provider, { cursor });
      setItems((prev) => (cursor ? [...prev, ...data.items] : data.items));
      setPageInfo(data.pageInfo);
    } catch (error) {
      showAlert('Failed to load mapping history', 'error', error);
    } finally {
      setLoading(false);
    }
  }, [provider, showAlert]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }
  if (items.length === 0) {
    return <EmptyState title="No changes yet" description="Every add, change, removal and import will be listed here." />;
  }

  return (
    <Box sx={{ pt: 2 }}>
      <ResultsCount count={pageInfo.totalCount ?? items.length} singular="change" plural="changes" />
      {groupHistoryByVersion(items).map((group) => (
        <HistoryGroup key={`v${group.setVersion}`}>
          <HistoryGroupHeader>
            <strong>{`Version ${group.setVersion}`}</strong>
            <span>{group.isImport ? `Import · ${group.entries.length} ${group.entries.length === 1 ? 'change' : 'changes'}` : 'Edit'}</span>
            <span>{group.changedBy}</span>
            <span>{new Date(group.changedAt).toLocaleString()}</span>
          </HistoryGroupHeader>
          {group.entries.map((entry) => {
            const { title, change } = describeHistoryRow(entry);
            return (
              <HistoryEntry key={entry.id}>
                <Chip size="small" label={shortLabel(entry.action)} sx={{ ...getChipSx(ACTION_COLORS[entry.action]), minWidth: 76 }} />
                <PatternCell>{title}</PatternCell>
                <HistorySeparator aria-hidden="true">—</HistorySeparator>
                <HistoryChange>{change}</HistoryChange>
              </HistoryEntry>
            );
          })}
        </HistoryGroup>
      ))}

      {pageInfo.hasNextPage && (
        <Box sx={{ textAlign: 'center' }}>
          <Button onClick={() => load(pageInfo.endCursor)} disabled={loading} sx={{ mt: 1 }}>
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HistoryPanel;
